import React, { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { findProductById, allProducts, computeLivePrice, getCoefficient, pricePer1000, getUnitLabel, getEffectiveMinQty } from '../lib/catalog';
import { PlatformLogo } from '../components/PlatformLogo';
import { toast } from '../hooks/use-toast';
import {
  Star, Zap, ShieldCheck, Lock, Truck, Users, TrendingUp,
  CheckCircle2, ChevronRight, Plus, Minus, Award, RefreshCw, Info
} from 'lucide-react';

const FAQItem = ({ q, a, open, onClick }) => (
  <div className={`glass-card rounded-2xl overflow-hidden transition ${open ? 'border-[#ff8247]/40' : ''}`}>
    <button onClick={onClick} className="w-full flex items-center justify-between p-5 text-left">
      <span className="text-white font-semibold text-sm md:text-base pr-4">{q}</span>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition flex-shrink-0 ${open ? 'bg-[#ff8247] text-[#0a0a0f]' : 'bg-white/5 text-white'}`}>
        {open ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
      </div>
    </button>
    {open && <div className="px-5 pb-5 text-white/60 leading-relaxed text-sm">{a}</div>}
  </div>
);

const Product = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const product = useMemo(() => findProductById(productId), [productId]);
  const [openFaq, setOpenFaq] = useState(0);
  const [qty, setQty] = useState(0);

  // Initialise qty from the effective min (guaranteed to reach ≥ 1 € sale price)
  React.useEffect(() => {
    if (product) {
      const smmMax = Number(product.max || 1000000);
      setQty(Math.min(smmMax, getEffectiveMinQty(product)));
    }
  }, [product]);

  if (!product) {
    return (
      <div>
        <AnnouncementBar />
        <Header />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <div className="glass-card rounded-3xl p-12">
            <div className="text-white text-xl font-semibold mb-3">Service introuvable</div>
            <p className="text-white/60 mb-6">Ce service n’existe pas ou a été retiré du catalogue.</p>
            <Link to="/collections" className="btn-primary text-sm inline-flex items-center gap-2">Retour au catalogue <ChevronRight className="w-4 h-4" /></Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const platform = product.platform;
  const related = allProducts.filter(p => p.platformKey === product.platformKey && p.id !== product.id).slice(0, 3);
  const rate = Number(product.rate || 0);
  const smmMax = Number(product.max || 1000000);
  // Vraie quantité minimum pour atteindre 1 € (aucun prix planché artificiel).
  const minQ = Math.min(smmMax, getEffectiveMinQty(product));
  const maxQ = smmMax;
  const coef = getCoefficient(product.platformKey);
  const livePrice = computeLivePrice(rate, qty, coef);
  const qtyValid = qty >= minQ && qty <= maxQ && Number.isFinite(qty);
  const per1000 = pricePer1000(product); // prix de vente /1000 (marge et arrondi commercial inclus)
  const formatUnit = () => rate > 0 ? `${per1000.toFixed(2)} € / 1 000 ${getUnitLabel(product.name)}` : '—';

  const handleBuyNow = () => {
    if (!qtyValid) {
      toast({ title: 'Quantité invalide', description: `La quantité doit être entre ${minQ.toLocaleString('fr-FR')} et ${maxQ.toLocaleString('fr-FR')}.` });
      return;
    }
    navigate(`/checkout/${product.id}?qty=${qty}`);
  };

  // Contextual description & FAQ
  const nameLower = product.name.toLowerCase();
  const isReview = nameLower.includes('avis') || nameLower.includes('review') || nameLower.includes('rating');
  const isFollowers = nameLower.includes('abonn') || nameLower.includes('follower') || nameLower.includes('member') || nameLower.includes('fan');
  const isViews = nameLower.includes('vue') || nameLower.includes('view') || nameLower.includes('play') || nameLower.includes('stream');
  const isLikes = nameLower.includes('like') || nameLower.includes('reaction') || nameLower.includes('vote') || nameLower.includes('upvote');

  const description = isReview
    ? `${product.name} — des avis authentiques rédigés par de vrais profils vérifiés, publiés progressivement sur votre fiche pour un effet 100% naturel. Boostez votre e-réputation en toute sécurité.`
    : isFollowers
    ? `Boostez votre présence sur ${platform?.name} avec ${product.name}. Livraison progressive et naturelle, sans mot de passe, pour renforcer votre crédibilité et attirer un vrai public organique.`
    : isViews
    ? `${product.name} pour propulser votre contenu dans l’algorithme ${platform?.name}. Vues réelles, rétention optimisée et livraison en douceur.`
    : isLikes
    ? `Ajoutez du social proof immédiat à votre contenu. ${product.name} livrés progressivement pour un effet organique, sans pic suspect.`
    : `${product.name} — un service premium de SociBoost. Livraison rapide, sécurisée et progressive, sans mot de passe.`;

  const faqs = [
    { q: `Comment fonctionne la livraison de ${product.name} ?`,
      a: `Dès validation de votre commande, notre système démarre la livraison en 12h maximum. La livraison est progressive sur quelques heures pour rester 100% naturelle aux yeux de l’algorithme ${platform?.name}.` },
    { q: 'Faut-il donner mon mot de passe ?',
      a: `Non, jamais. Nous n’avons besoin que du lien public de votre ${isReview ? 'fiche' : 'profil ou publication'} ${platform?.name}. Votre compte reste 100% privé et sécurisé.` },
    { q: 'Y a-t-il un risque pour mon compte ?',
      a: `Aucun. Notre méthode respecte les conditions d’utilisation de ${platform?.name}. Depuis 2021, aucun de nos clients n’a été sanctionné pour l’utilisation de nos services.` },
    { q: 'Que couvre la garantie ?',
      a: `Nous offrons jusqu’à 2 renvois gratuits en cas de perte partielle. Si votre commande n’est pas délivrée dans les délais, nous remboursons intégralement.` },
    { q: 'Comment suivre ma commande ?',
      a: `Créez un compte SociBoost pour suivre chaque livraison en temps réel depuis votre espace client. Vous recevez aussi un email à chaque étape.` }
  ];

  const guarantees = [
    { i: Zap, t: 'Livraison 12h', d: 'Progressive et naturelle' },
    { i: ShieldCheck, t: 'Sans mot de passe', d: '100% sécurisé' },
    { i: RefreshCw, t: 'Renvoi garanti', d: 'Jusqu’à 2 renvois offerts' },
    { i: Award, t: 'Qualité premium', d: `Optimisé pour ${platform?.name}` }
  ];

  return (
    <div>
      <AnnouncementBar />
      <Header />

      <div className="relative border-b border-white/5">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute -top-32 -right-24 w-[400px] h-[400px] rounded-full blur-[180px] opacity-15" style={{ background: platform?.color }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-8">
          <div className="flex items-center gap-2 text-white/50 text-xs mb-6">
            <Link to="/" className="hover:text-white transition">Accueil</Link>
            <span>/</span>
            <Link to="/collections" className="hover:text-white transition">Boutique</Link>
            <span>/</span>
            <Link to={`/collections/${platform?.slug}`} className="hover:text-white transition">{platform?.name}</Link>
            <span>/</span>
            <span className="text-white truncate">{product.name}</span>
          </div>
        </div>
      </div>

      <section className="relative max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-8 lg:py-14 grid lg:grid-cols-12 gap-6 lg:gap-10">
        <div className="order-2 lg:order-1 lg:col-span-7 min-w-0">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${platform?.color}18` }}>
              <PlatformLogo platform={platform?.id} className="w-8 h-8" color={platform?.id === 'instagram' ? undefined : platform?.color} />
            </div>
            <div>
              <div className="text-white/50 text-xs font-semibold uppercase tracking-widest">{platform?.name}</div>
              <div className="flex items-center gap-2 text-[#00b67a] text-xs font-semibold mt-0.5">
                <Star className="w-3.5 h-3.5" fill="currentColor" /> 4.8 sur Trustpilot · <span className="text-white/50">2 341 clients satisfaits</span>
              </div>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-4">
            {product.name.split(' ').slice(0, -1).join(' ')} <span className="gradient-text">{product.name.split(' ').slice(-1)}</span>
          </h1>
          <p className="text-white/60 leading-relaxed max-w-2xl mb-8 text-base">{description}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            {guarantees.map((g, i) => (
              <div key={i} className="glass-card rounded-2xl p-4">
                <div className="w-9 h-9 rounded-xl bg-[#ff8247]/10 flex items-center justify-center mb-3">
                  <g.i className="w-4 h-4 text-[#ff8247]" />
                </div>
                <div className="text-white text-sm font-semibold">{g.t}</div>
                <div className="text-white/50 text-xs mt-0.5">{g.d}</div>
              </div>
            ))}
          </div>

          <div className="mb-10">
            <div className="text-[#ff8247] text-xs font-bold uppercase tracking-widest mb-4">Comment ça marche</div>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { i: 1, t: 'Choisissez votre pack', d: 'Sélectionnez la quantité qui correspond à vos objectifs.' },
                { i: 2, t: 'Renseignez votre lien', d: `Ajoutez le lien public de votre ${isReview ? 'fiche' : 'profil'} ${platform?.name}, sans mot de passe.` },
                { i: 3, t: 'Recevez votre boost', d: 'La livraison démarre en moins de 12h, progressive et naturelle.' }
              ].map((s) => (
                <div key={s.i} className="glass-card rounded-2xl p-5">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#ff8247] to-[#ff2d6f] flex items-center justify-center text-[#0a0a0f] font-black text-sm mb-3">{s.i}</div>
                  <div className="text-white text-sm font-semibold mb-1">{s.t}</div>
                  <div className="text-white/50 text-xs leading-relaxed">{s.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div id="faq" className="mb-10">
            <div className="text-[#ff8247] text-xs font-bold uppercase tracking-widest mb-4">FAQ spécifique</div>
            <div className="space-y-2">
              {faqs.map((f, i) => (
                <FAQItem key={i} q={f.q} a={f.a} open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </div>
          </div>

          {related.length > 0 && (
            <div>
              <div className="text-[#ff8247] text-xs font-bold uppercase tracking-widest mb-4">Vous aimerez aussi</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {related.map(r => (
                  <button key={r.id} onClick={() => { navigate(`/product/${r.id}`); window.scrollTo(0, 0); }}
                    className="glass-card rounded-2xl p-4 text-left hover:border-[#ff8247]/40 transition">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${platform?.color}18` }}>
                        <PlatformLogo platform={platform?.id} className="w-4 h-4" color={platform?.id === 'instagram' ? undefined : platform?.color} />
                      </div>
                      <span className="text-white/60 text-xs font-semibold">{platform?.name}</span>
                    </div>
                    <div className="text-white text-sm font-semibold mb-1 line-clamp-1">{r.name}</div>
                    <div className="text-white/50 text-xs">À partir de {pricePer1000(r).toFixed(2)} € / 1 000 {getUnitLabel(r.name)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="order-1 lg:order-2 lg:col-span-5 min-w-0">
          <div className="glass-card rounded-3xl p-6 lg:p-7 sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <span className="pulse-dot" />
              <span className="text-white/70 text-xs font-semibold uppercase tracking-widest">En stock · livraison 12h</span>
            </div>

            <div className="text-white/60 text-xs mb-2 font-semibold">Choisissez votre quantité</div>
            <div className="mb-4">
              <div className="relative">
                <input
                  data-testid="product-qty-input"
                  type="number"
                  value={qty || ''}
                  onChange={e => setQty(parseInt(e.target.value) || 0)}
                  min={minQ}
                  max={maxQ}
                  step="1"
                  className={`w-full bg-white/5 border rounded-xl px-4 py-4 text-white text-2xl font-bold focus:outline-none transition ${qtyValid ? 'border-white/10 focus:border-[#ff8247]' : 'border-red-500/60'}`}
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  placeholder={`${minQ}`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">unités</div>
              </div>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="text-white/50">Min : <span className="text-white font-semibold">{minQ.toLocaleString('fr-FR')}</span></span>
                <span className="text-white/50">Max : <span className="text-white font-semibold">{maxQ.toLocaleString('fr-FR')}</span></span>
              </div>
              {!qtyValid && qty > 0 && (
                <div className="mt-2 text-red-400 text-xs font-semibold">
                  {qty < minQ ? `Minimum : ${minQ.toLocaleString('fr-FR')} unités` : `Maximum : ${maxQ.toLocaleString('fr-FR')} unités`}
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 gap-2 mb-6">
              {[minQ, Math.max(minQ, 1000), Math.max(minQ, 5000), Math.min(maxQ, 10000)].filter((v, i, a) => v <= maxQ && a.indexOf(v) === i).slice(0, 4).map(v => (
                <button key={v} onClick={() => setQty(v)}
                  className="py-2 rounded-lg bg-white/5 hover:bg-[#ff8247]/10 hover:border-[#ff8247]/30 border border-white/10 text-white/80 text-xs font-semibold transition">
                  {v.toLocaleString('fr-FR')}
                </button>
              ))}
            </div>

            <div className="border-t border-white/5 pt-5 mb-5">
              <div className="flex items-end justify-between mb-2">
                <div>
                  <div className="text-white/50 text-xs">Prix total</div>
                  <div data-testid="product-live-price" className="text-4xl font-bold text-white transition-all" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {livePrice.toFixed(2)} <span className="text-lg text-white/60">€</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white/50 text-xs">Tarif indicatif</div>
                  <div className="text-[#ff8247] text-sm font-semibold">{formatUnit()}</div>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-5">
              <button onClick={handleBuyNow} disabled={!qtyValid} data-testid="product-buy-now-btn"
                className="btn-primary w-full justify-center inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <Lock className="w-4 h-4" /> Payer maintenant · {livePrice.toFixed(2)} €
              </button>
            </div>

            <div className="space-y-2 pt-4 border-t border-white/5">
              {[
                { i: CheckCircle2, t: 'Sans mot de passe requis' },
                { i: Truck, t: 'Livraison en moins de 12h' },
                { i: Users, t: 'Profils de haute qualité' },
                { i: TrendingUp, t: 'Progression naturelle' }
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-white/70 text-xs">
                  <f.i className="w-4 h-4 text-[#00b67a]" /> {f.t}
                </div>
              ))}
            </div>

            <div className="mt-5 p-3 rounded-xl bg-[#ff8247]/5 border border-[#ff8247]/20 text-white/70 text-xs flex items-start gap-2">
              <Info className="w-4 h-4 text-[#ff8247] flex-shrink-0 mt-0.5" />
              <span>Vous serez guidé pour renseigner votre lien après le paiement. Aucune donnée sensible demandée.</span>
            </div>
          </div>
        </aside>
      </section>

      <Footer />
    </div>
  );
};

export default Product;
